import { macOS } from './utils/macOS';
import { isNumber, isUndefined } from './utils';
import { TransformUnit } from './services/TransformUnit';
import { ARIA_ACTIVEDESCENDANT, ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ARROW_UP, BLUR, KEY_END, FOCUS, KEY_HOME, KEYDOWN, MOUSEDOWN, POINTERDOWN, SELECT, TABINDEX, TOUCHSTART, SHAPE } from './constants';
/** @hidden */
export function navigationElements(shapes, connections) {
    const result = [];
    const addedConnections = new Set();
    // Sort shapes by reading order: top-to-bottom, then left-to-right
    const sortedShapes = [...shapes].sort((a, b) => {
        const boundsA = a.bounds();
        const boundsB = b.bounds();
        // First by y (top)
        if (boundsA.y !== boundsB.y) {
            return boundsA.y - boundsB.y;
        }
        // Then by x (left)
        return boundsA.x - boundsB.x;
    });
    // Create a map for quick shape index lookup
    const shapeIndexMap = new Map();
    sortedShapes.forEach((shape, index) => {
        shapeIndexMap.set(shape, index);
    });
    // For each shape, add it and its outgoing connections
    for (const shape of sortedShapes) {
        result.push(shape);
        // Find outgoing connections (where from = current shape)
        const outgoingConnections = connections.filter(conn => conn.from === shape && !addedConnections.has(conn));
        // Sort outgoing connections by target shape order
        const sortedOutgoing = outgoingConnections.sort((a, b) => {
            var _a, _b;
            const indexA = a.to ? (_a = shapeIndexMap.get(a.to)) !== null && _a !== void 0 ? _a : Infinity : Infinity;
            const indexB = b.to ? (_b = shapeIndexMap.get(b.to)) !== null && _b !== void 0 ? _b : Infinity : Infinity;
            return indexA - indexB;
        });
        // Add sorted outgoing connections
        for (const conn of sortedOutgoing) {
            result.push(conn);
            addedConnections.add(conn);
        }
    }
    // Append any remaining connections that weren't added
    for (const conn of connections) {
        if (!addedConnections.has(conn)) {
            result.push(conn);
        }
    }
    return result;
}
/** @hidden */
export class Navigation {
    constructor(diagram) {
        this.elements = [];
        this._skipAutoFocus = false;
        this._selectHandler = (e) => {
            if (this.isDisabled()) {
                return;
            }
            if (e.selected && e.selected.length > 0) {
                this.elements = navigationElements(e.sender.shapes, e.sender.connections);
                const index = this.elements.indexOf(e.selected[0]);
                if (index !== -1) {
                    this.currentIndex = index;
                    if (e.selected.length === 1) {
                        this.focusSelectedElement();
                    }
                }
            }
            else {
                this.diagram.element.removeAttribute(ARIA_ACTIVEDESCENDANT);
                this.blurAll();
            }
        };
        this._keydownHandler = (e) => {
            var _a, _b;
            // Handle keydown events for navigation
            if (this.isDisabled() || !this._hasFocus()) {
                return;
            }
            const meta = this.diagram._meta(e);
            const macCmd = meta.metaKey && macOS();
            const ctrlKey = meta.ctrlKey || macCmd;
            if (!ctrlKey) {
                switch (e.key) {
                    case ARROW_RIGHT:
                        this.focusNext();
                        e.preventDefault();
                        break;
                    case ARROW_LEFT:
                        this.focusPrevious();
                        e.preventDefault();
                        break;
                    case KEY_HOME:
                        this.focusFirst();
                        e.preventDefault();
                        break;
                    case KEY_END:
                        this.focusLast();
                        e.preventDefault();
                        break;
                    default:
                        break;
                }
            }
            else {
                // Ctrl/Cmd + Arrow keys to move the focused element
                const currentElement = this.elements[this.currentIndex];
                if (!currentElement)
                    return;
                let moveBy = (_a = this.diagram.options.navigation) === null || _a === void 0 ? void 0 : _a.smallStep;
                if (e.shiftKey) {
                    moveBy = (_b = this.diagram.options.navigation) === null || _b === void 0 ? void 0 : _b.largeStep;
                }
                if (currentElement.name !== SHAPE || !isNumber(moveBy)) {
                    return;
                }
                const shape = currentElement;
                const initialBounds = shape.bounds();
                const position = shape.position().clone();
                switch (e.key) {
                    case ARROW_RIGHT:
                        position.x += moveBy;
                        e.preventDefault();
                        break;
                    case ARROW_LEFT:
                        position.x -= moveBy;
                        e.preventDefault();
                        break;
                    case ARROW_UP:
                        position.y -= moveBy;
                        e.preventDefault();
                        break;
                    case ARROW_DOWN:
                        position.y += moveBy;
                        e.preventDefault();
                        break;
                    default:
                        break;
                }
                if (!position.equals(shape.position())) {
                    shape.position(position);
                    const unit = new TransformUnit([shape], [initialBounds]);
                    this.diagram.undoRedoService.add(unit, false);
                }
            }
        };
        this._downHandler = () => {
            if (this.isDisabled()) {
                return;
            }
            if (!this._hasFocus()) {
                this._skipAutoFocus = true;
            }
        };
        this._focusHandler = () => {
            if (this.isDisabled()) {
                return;
            }
            if (this._skipAutoFocus) {
                this._skipAutoFocus = false;
                // If we already have a valid current element (from previous selection),
                // focus it even though we're skipping auto-focus
                if (this.elements.length > 0 && this.currentIndex >= 0 && this.currentIndex < this.elements.length) {
                    this.focusSelectedElement();
                }
                return;
            }
            this.elements = navigationElements(this.diagram.shapes, this.diagram.connections);
            this.updateFocus();
        };
        this._blurHandler = () => {
            if (this.isDisabled()) {
                return;
            }
            this.blurAll();
        };
        this.currentIndex = 0;
        this.diagram = diagram;
        this.attachEvents();
        this.setTabIndex();
    }
    setTabIndex() {
        const { options, element } = this.diagram;
        if (!element.hasAttribute(TABINDEX) && !isUndefined(options.tabIndex)) {
            element.setAttribute(TABINDEX, String(options.tabIndex));
        }
    }
    attachEvents() {
        this.diagram.element.addEventListener(KEYDOWN, this._keydownHandler);
        this.diagram.element.addEventListener(MOUSEDOWN, this._downHandler);
        this.diagram.element.addEventListener(TOUCHSTART, this._downHandler);
        this.diagram.element.addEventListener(POINTERDOWN, this._downHandler);
        this.diagram.element.addEventListener(FOCUS, this._focusHandler);
        this.diagram.element.addEventListener(BLUR, this._blurHandler);
        this.diagram.bind(SELECT, this._selectHandler);
    }
    isDisabled() {
        var _a, _b;
        return (_b = (_a = this.diagram) === null || _a === void 0 ? void 0 : _a.options.navigation) === null || _b === void 0 ? void 0 : _b.disabled;
    }
    detachEvents() {
        this.diagram.element.removeEventListener(KEYDOWN, this._keydownHandler);
        this.diagram.element.removeEventListener(MOUSEDOWN, this._downHandler);
        this.diagram.element.removeEventListener(TOUCHSTART, this._downHandler);
        this.diagram.element.removeEventListener(POINTERDOWN, this._downHandler);
        this.diagram.element.removeEventListener(FOCUS, this._focusHandler);
        this.diagram.element.removeEventListener(BLUR, this._blurHandler);
        this.diagram.unbind(SELECT, this._selectHandler);
    }
    _hasFocus() {
        var _a;
        return ((_a = this.diagram) === null || _a === void 0 ? void 0 : _a.element.ownerDocument.activeElement) === this.diagram.element;
    }
    destroy() {
        if (this.diagram) {
            this.detachEvents();
        }
        this.elements = [];
        this.currentIndex = -1;
        this.diagram = null;
    }
    focusNext() {
        if (this.elements.length === 0) {
            return;
        }
        this.currentIndex = (this.currentIndex + 1) % this.elements.length;
        this.updateFocus();
    }
    focusPrevious() {
        if (this.elements.length === 0) {
            return;
        }
        this.currentIndex = (this.currentIndex - 1 + this.elements.length) % this.elements.length;
        this.updateFocus();
    }
    focusFirst() {
        if (this.elements.length === 0) {
            return;
        }
        this.currentIndex = 0;
        this.updateFocus();
    }
    focusLast() {
        if (this.elements.length === 0) {
            return;
        }
        this.currentIndex = this.elements.length - 1;
        this.updateFocus();
    }
    selectCurrent() {
        this.elements[this.currentIndex].select(true);
        this.focusSelectedElement();
    }
    deselectAll() {
        this.elements.forEach(el => {
            el.select(false);
            el.blur();
        });
        this.diagram.element.removeAttribute(ARIA_ACTIVEDESCENDANT);
    }
    focusSelectedElement() {
        const element = this.elements[this.currentIndex];
        this.diagram.element.setAttribute(ARIA_ACTIVEDESCENDANT, element.options.id);
        this.blurAll();
        element.focus();
    }
    blurAll() {
        this.elements.forEach(el => el.blur());
    }
    updateFocus() {
        if (this.elements.length === 0 || this.currentIndex < 0 || this.currentIndex >= this.elements.length) {
            return;
        }
        this.deselectAll();
        this.selectCurrent();
    }
}
